import { useCallback, useEffect, useState } from "react";

/**
 * Detects when the server is serving a newer build than this tab loaded (SIM-1670). The build id is
 * read at runtime from `<meta name="build-id">`, never a build-time `define`: Railway populates
 * `RAILWAY_GIT_COMMIT_SHA` only at runtime, so a bundled constant always resolves to "". Polls
 * `/version.json` and compares. Detection only — it never reloads.
 */

const DEFAULT_ENDPOINT = "/version.json";
const DEFAULT_POLL_INTERVAL_MS = 3 * 60 * 1000;

/** Read the build id stamped into `<meta name="build-id">`. "" in local dev. */
export function readBuildIdMeta(): string {
  if (typeof document === "undefined") return "";
  return document.querySelector<HTMLMetaElement>('meta[name="build-id"]')?.content ?? "";
}

/** Shape returned by `GET /version.json`. */
export interface VersionResponse {
  buildId: string;
}

async function defaultFetchVersion(endpoint: string): Promise<VersionResponse | null> {
  try {
    const res = await fetch(endpoint, {
      cache: "no-store",
      headers: { accept: "application/json" },
    });
    if (!res.ok) return null;
    const data: unknown = await res.json();
    if (data && typeof data === "object" && typeof (data as VersionResponse).buildId === "string") {
      return { buildId: (data as VersionResponse).buildId };
    }
    return null;
  } catch {
    // Network blip / offline — treat as "no new info", retry next poll.
    return null;
  }
}

export interface UseVersionCheckOptions {
  /** Endpoint serving `{ buildId }`. Default `/version.json`. */
  endpoint?: string;
  /** Poll cadence in ms. Default 3 min. */
  pollIntervalMs?: number;
  /**
   * Force-disable. Default: enabled only when a non-empty build id is present
   * (so local dev — empty meta tag — never polls and HMR is undisturbed).
   */
  enabled?: boolean;
  /** Injectable for tests; defaults to fetching `endpoint`. */
  fetchVersion?: (endpoint: string) => Promise<VersionResponse | null>;
  /** Injectable for tests; defaults to reading the `build-id` meta tag. */
  readBuildId?: () => string;
}

export interface VersionCheckResult {
  /** A different, non-empty build id is being served than this tab loaded. */
  newVersionAvailable: boolean;
  /** Build id this tab loaded ("" in dev). */
  loadedBuildId: string;
  /** Latest build id seen from the endpoint, or null before the first poll. */
  latestBuildId: string | null;
}

export function useVersionCheck(options: UseVersionCheckOptions = {}): VersionCheckResult {
  const {
    endpoint = DEFAULT_ENDPOINT,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    fetchVersion = defaultFetchVersion,
    readBuildId = readBuildIdMeta,
  } = options;

  const [loadedBuildId] = useState(readBuildId);
  const [latestBuildId, setLatestBuildId] = useState<string | null>(null);

  // Disabled in dev (no build id) or when explicitly turned off. A new editor
  // can't accidentally enable polling in dev because the meta tag is empty.
  const enabled = options.enabled ?? loadedBuildId !== "";

  const poll = useCallback(() => {
    void fetchVersion(endpoint).then((res) => {
      if (res) setLatestBuildId(res.buildId);
    });
  }, [fetchVersion, endpoint]);

  useEffect(() => {
    if (!enabled) return;
    poll();
    const interval = setInterval(poll, pollIntervalMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, pollIntervalMs, poll]);

  const newVersionAvailable =
    enabled && latestBuildId !== null && latestBuildId !== "" && latestBuildId !== loadedBuildId;

  return { newVersionAvailable, loadedBuildId, latestBuildId };
}
